import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Form, Table } from 'react-bootstrap';
import { ApiGet } from '../../../../ApiServices/ApiServices';
import { toast } from 'react-toastify';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('daily');
  const [reportData, setReportData] = useState(null);

  // Date filters
  const [filters, setFilters] = useState({
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchReportData();
  }, [reportType]);

  const fetchReportData = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        reportType,
        fromDate: filters.fromDate,
        toDate: filters.toDate
      };

      const response = await ApiGet('/api/kitchen/reports', params);

      if (response.success) {
        setReportData(response.success?.data?.data || generateMockData());
      } else {
        // Use mock data for now
        setReportData(generateMockData());
      }
    } catch (err) {
      // Use mock data if API fails
      setReportData(generateMockData());
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = () => {
    return {
      summary: {
        totalOrders: 0,
        completedOrders: 0,
        avgPrepTime: 0,
        peakHour: '-'
      },
      ordersByStatus: [
        { status: 'PENDING', count: 0 },
        { status: 'PREPARING', count: 0 },
        { status: 'READY', count: 0 },
        { status: 'DELIVERED', count: 0 },
        { status: 'CANCELLED', count: 0 }
      ],
      topItems: [],
      hourlyBreakdown: []
    };
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateReport = () => {
    fetchReportData();
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body className="d-flex align-items-center">
        <div className="me-3" style={{
          width: 50,
          height: 50,
          borderRadius: '50%',
          backgroundColor: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <i className={`${icon} fs-4`} style={{ color }}></i>
        </div>
        <div>
          <div className="text-muted small">{title}</div>
          <div className="fs-4 fw-bold" style={{ color }}>{value}</div>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
          <i className="bi bi-graph-up me-2"></i>
          Kitchen Reports
        </h3>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">Report Type</Form.Label>
                <Form.Select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  size="sm"
                >
                  <option value="daily">Daily Report</option>
                  <option value="weekly">Weekly Report</option>
                  <option value="monthly">Monthly Report</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={6} md={12}>
              <Button
                variant="primary"
                size="sm"
                onClick={handleGenerateReport}
                disabled={loading}
                style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : (
                  <i className="bi bi-file-earmark-bar-graph me-1"></i>
                )}
                Generate Report
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Generating report...</div>
        </div>
      ) : reportData ? (
        <>
          {/* Summary Stats */}
          <Row className="g-3 mb-4">
            <Col lg={3} md={6}>
              <StatCard
                title="Total Orders"
                value={reportData.summary?.totalOrders || 0}
                icon="bi bi-cart3"
                color="#3b82f6"
              />
            </Col>
            <Col lg={3} md={6}>
              <StatCard
                title="Completed"
                value={reportData.summary?.completedOrders || 0}
                icon="bi bi-check-circle"
                color="#10b981"
              />
            </Col>
            <Col lg={3} md={6}>
              <StatCard
                title="Avg Prep Time"
                value={`${reportData.summary?.avgPrepTime || 0} min`}
                icon="bi bi-clock"
                color="#f59e0b"
              />
            </Col>
            <Col lg={3} md={6}>
              <StatCard
                title="Peak Hour"
                value={reportData.summary?.peakHour || '-'}
                icon="bi bi-graph-up-arrow"
                color="#8b5cf6"
              />
            </Col>
          </Row>

          <Row className="g-3">
            {/* Orders by Status */}
            <Col lg={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-white border-0 py-3">
                  <h5 className="mb-0 fw-bold">Orders by Status</h5>
                </Card.Header>
                <Card.Body>
                  {reportData.ordersByStatus?.length > 0 ? (
                    <Table hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0">Status</th>
                          <th className="border-0 text-end">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.ordersByStatus.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.status}</td>
                            <td className="text-end fw-bold">{item.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center text-muted py-4">
                      No data available
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Top Menu Items */}
            <Col lg={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-white border-0 py-3">
                  <h5 className="mb-0 fw-bold">Top Menu Items</h5>
                </Card.Header>
                <Card.Body>
                  {reportData.topItems?.length > 0 ? (
                    <Table hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0">#</th>
                          <th className="border-0">Item Name</th>
                          <th className="border-0 text-end">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.topItems.map((item, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{item.name}</td>
                            <td className="text-end fw-bold">{item.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center text-muted py-4">
                      No data available
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Hourly Breakdown */}
          <Row className="g-3 mt-3">
            <Col lg={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-0 py-3">
                  <h5 className="mb-0 fw-bold">Hourly Order Breakdown</h5>
                </Card.Header>
                <Card.Body>
                  {reportData.hourlyBreakdown?.length > 0 ? (
                    <Table responsive hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0">Hour</th>
                          <th className="border-0 text-end">Orders</th>
                          <th className="border-0 text-end">Avg Prep Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.hourlyBreakdown.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.hour}</td>
                            <td className="text-end">{item.orders}</td>
                            <td className="text-end">{item.avgPrepTime} min</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center text-muted py-4">
                      No hourly data available for the selected period
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <i className="bi bi-bar-chart fs-1 text-muted d-block mb-3" style={{ opacity: 0.5 }}></i>
            <h5 className="text-muted">No Report Data</h5>
            <p className="text-muted mb-0">Select a date range and click Generate Report to view data.</p>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default Reports;
